import { daysRemaining } from 'components/offer/utils';
import { OfferImage } from 'components/offer/card/OfferImage';
import { localize } from 'i18n/localize.ts';
import { type OfferData } from 'service/offer/model.ts';
import { offerPrice } from 'utils/format.tsx';
import { cn } from 'utils/cn';
import { getShortDisplayedOfferLocality } from 'utils/getLocalityName';
import { isInactive, OfferStatusAsText } from 'components/offer/utils/status';
import { ProcessingVideoOverlay } from 'components/offer/card/VideoProcessingOverlay';
import './OfferCard.less';
import { OfferRemainingDays } from 'components/offer/OfferRemainingDays';

const $t = localize();

type Props = {
	offer: OfferData;
	lazyImage?: boolean;
	showVideoStatus?: boolean;
	isAdmin?: boolean;
};

/**
 * For easier styling this component relies on tailwind and also on its sibling .less file for nested CSS selectors.
 */
export function OfferCard({ offer, lazyImage, showVideoStatus, isAdmin }: Props) {
	const isOfferInactive = isInactive(offer.status);
	const days = offer.validTo ? daysRemaining(offer.validTo) : null;
	const hasProcessingVideo = offer.videos?.some((video) => video.status === 'processing') ?? false;
	return (
		<div class="offer-card w-full h-full relative overflow-hidden flex flex-col">
			{/* Use the order-x to specify the actual element position. Having text first in HTML should improve SEO. */}
			<div class="py-3 @cardLg:px-2 pb-0 order-2">
				<div class="text-red group-hover/card:text-red-hover line-clamp-2 overflow-ellipsis leading-6 text-left">
					{offer.name || $t.offer.placeholder.name}
				</div>
				<div class="mt-1 line-clamp-1 overflow-ellipsis text-dark-blue-50 flex">
					<b class="text-neutral-black flex-none">
						{offer.price !== undefined && !Number.isNaN(offer.price)
							? offerPrice(offer)
							: $t.offer.placeholder.price}
					</b>
					&nbsp;
					<span class="whitespace-pre text-dark-blue-50 shrink group-hover/card:text-dark-blue truncate">
						{offer.locality !== undefined
							? `${$t.offerLocality.at} ${getShortDisplayedOfferLocality(offer.locality)}`
							: $t.offer.placeholder.locality}{' '}
					</span>
					{!!offer.otherLocalitiesCount && (
						<span class="min-w-9 truncate shrink-[1000] text-dark-blue-50 group-hover/card:text-dark-blue">
							{`+${offer.otherLocalitiesCount} ${$t.offerLocality.more}`}
						</span>
					)}
				</div>
				{isAdmin && (
					<div class="mb-1">
						<span class="leading-6 text-dark-blue-50 group-hover/card:text-dark-blue">
							<OfferStatusAsText offer={offer} />
							{!isOfferInactive && <OfferRemainingDays days={days} />}
						</span>
						{!isOfferInactive && (
							<>
								<br />
								<span class="text-dark-blue-50 group-hover/card:text-dark-blue">
									{offer.statistics?.detailView ?? 0} {$t.offer.displays}
								</span>
							</>
						)}
					</div>
				)}
			</div>
			<div
				class={cn(
					isOfferInactive && 'grayscale hover:filter-none',
					'relative offer-card__image-wrapper rounded-2xl bg-gray-card flex items-center justify-center order-1 overflow-hidden',
				)}
			>
				{showVideoStatus && hasProcessingVideo ? (
					<ProcessingVideoOverlay className="absolute" />
				) : (
					<OfferImage
						alt={offer.name}
						className="group-hover/card:scale-105 transition-all duration-500"
						images={offer.images}
						lazy={lazyImage}
					/>
				)}
			</div>
		</div>
	);
}
